<template>
  <!-- Title -->
  <h2>
    Plantillas de WhatsApp
    <div class="subtitulo" v-if="!isViewTemplates">
      del número +{{ associate }}
      <a-tooltip>
        <template #title>
          Solicita nuevas plantillas predefinidas de WhatsApp.
          <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
        </template>

        <InfoCircleOutlined class="icon" />
      </a-tooltip>
    </div>
    <a-select
      :getPopupContainer="(triggerNode) => triggerNode.parentNode"
      ref="select"
      v-model:value="selectedIdentifier"
      placeholder="Seleccionar identificador"
      style="width: 250px; margin-left: 20px"
      :disabled="optionsIdentifier.length == 0"
      @change="updateTableData"
      v-else
    >
      <a-select-option v-for="option in optionsIdentifier" :key="option.id" :value="option.id">
        +{{ option.associate }} ( {{ option.alias }} )
      </a-select-option>
    </a-select>
  </h2>
  <p>
    Solicita tus plantillas de WhatsApp para el número seleccionado. Recuerda que cada identificador
    cuenta con sus plantillas únicas.
    <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
  </p>
  <!-- Controls -->
  <div class="table--controls">
    <a-form layout="inline" ref="formRef" :model="formState">
      <a-form-item>
        <a-input
          v-model:value="formState.search"
          placeholder="Buscar por nombre"
          class="form__input"
        >
          <template #prefix>
            <SearchOutlined />
          </template>
        </a-input>
      </a-form-item>

      <a-form-item>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.category"
          class="form__item"
          placeholder="Seleccionar categoría"
          :options="templateCategoriesOptions"
          :disabled="optionsIdentifier.length == 0 && isViewTemplates"
        />
      </a-form-item>

      <a-form-item>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.state"
          class="form__item"
          placeholder="Seleccionar estado"
          :options="templateStatesOptions"
          :disabled="optionsIdentifier.length == 0 && isViewTemplates"
        />
      </a-form-item>

      <a-form-item>
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.status"
          class="form__item"
          placeholder="Seleccionar"
          :options="templateStatusOptions"
          :disabled="optionsIdentifier.length == 0 && isViewTemplates"
        />
      </a-form-item>
    </a-form>

    <!-- Add template -->
    <a-button
      type="primary"
      :disabled="(optionsIdentifier.length == 0 || !selectedIdentifier) && isViewTemplates"
      @click="emit('openCreateModal', selectedIdentifier)"
    >
      Solicitar plantilla
    </a-button>
  </div>

  <!-- Templates table -->
  <a-table
    class="table"
    size="middle"
    :columns="whatsappTemplateTableColumns"
    :dataSource="data"
    :scroll="scrollStyle"
    :pagination="false"
    :loading="loadingTable"
    v-if="(data.length > 0 && isViewTemplates) || !isViewTemplates || loadingTable"
  >
    <!-- Type -->
    <template #template_type="{ text: type }">
      <span v-if="type === WhatsappTemplateTypes.Text">Texto</span>
      <span v-if="type === WhatsappTemplateTypes.TextAndImage">Texto e imagen</span>
      <span v-if="type === WhatsappTemplateTypes.TextAndVideo">Texto y video</span>
      <span v-if="type === WhatsappTemplateTypes.TextAndDocument">Texto y documento</span>
    </template>

    <!-- Assigned queues -->
    <template #queues="{ text: queues }">
      <a-popover v-if="queues?.length" title="Campañas y colas">
        <template #content>
          <ul>
            <li v-for="queue in queues" :key="queue.id">
              {{ queue?.portfolios?.[0]?.name }} - {{ queue?.name }}
            </li>
          </ul>
        </template>

        <span>
          <InfoCircleOutlined />
          {{ queues.length }} {{ queues.length === 1 ? "cola" : "colas" }}
        </span>
      </a-popover>

      <span class="info" v-else>Sin colas</span>
    </template>

    <!-- State -->
    <template #provider_state="{ text: provider_state }">
      <a-tag :color="stateTag?.[provider_state ?? WhatsappTemplateStates.Pending]?.color">
        {{ stateTag?.[provider_state ?? WhatsappTemplateStates.Pending]?.text }}
      </a-tag>
    </template>

    <!-- Status -->
    <template #status="{ record, text: status }">
      <a-switch
        v-if="record.provider_state === WhatsappTemplateStates.Approved"
        :checked="status === WhatsappTemplateStatus.Active"
        @change="handleToggleState(record)"
      />

      <span v-else> - </span>
    </template>

    <!-- Status -->
    <template #buttons="{ record, text: buttons }">
      <a-popover v-if="buttons?.length" :title="`Botones (${buttons?.length})`">
        <template #content>
          <h4>Tipo: {{ typeButtonsText(buttons[0].type) }}</h4>
          <div v-for="button in buttons" :key="button.id">
            <div class="button__name">
              <IconPhone
                v-if="button.type === WhatsappTemplateTypeSpecificButtons.PhoneNumber"
              />
              <IconWhatsappVoice
                v-if="button.type === WhatsappTemplateTypeSpecificButtons.VoiceCall"
              />
              <IconLink
                v-if="button.type === WhatsappTemplateTypeSpecificButtons.Url"
              />
              <IconMessage
                v-if="button.type === WhatsappTemplateTypeButtons.QuickReply"
              />
              <span class="button__name-type" v-if="button.type === WhatsappTemplateTypeSpecificButtons.PhoneNumber">Llamada: </span>
              <span class="button__name-type" v-if="button.type === WhatsappTemplateTypeSpecificButtons.VoiceCall">Llamada: </span>
              <span class="button__name-type" v-if="button.type === WhatsappTemplateTypeSpecificButtons.Url">URL: </span>
              <span>{{ button.text }}</span>
            </div>
            <div class="button__detail" v-if="button.type === WhatsappTemplateTypeSpecificButtons.Url">
              <a :href="button.url" target="_blank" rel="url-button">
                {{ button.url }}
              </a>
            </div>
            <div class="button__call" v-if="button.type === WhatsappTemplateTypeSpecificButtons.PhoneNumber">
              Número: {{ button.code_phone }} {{ button.phone }}
            </div>
            <div class="button__call" v-if="button.type === WhatsappTemplateTypeSpecificButtons.VoiceCall">
              Vía WhatsApp Voice
            </div>
          </div>          
        </template>
        <InfoCircleOutlined />
        <span v-if="buttons?.length > 1">{{ ` ${buttons?.length}` }} botones</span>
        <span v-else-if="buttons?.length == 1"> 1 botón</span>
      </a-popover>

      <span v-else class="info" style="color">Sin botones</span>
    </template>

    <!-- Actions -->
    <template #actions="{ record }">
      <div class="actions__wrapper">
        <!-- Template request preview -->
        <a-tooltip v-if="record?.provider_state !== WhatsappTemplateStates.Approved">
          <template #title>Visualizar solicitud</template>

          <a-button class="table--button" type="link" @click="emit('openPreviewModal', record)">
            <EyeOutlined />
          </a-button>
        </a-tooltip>

        <!-- Edit template -->
        <a-tooltip v-else>
          <template #title>Editar plantilla</template>

          <a-button class="table--button" type="link" @click="emit('openEditModal', record)">
            <EditOutlined />
          </a-button>
        </a-tooltip>

        <!-- Assign queue -->
        <a-tooltip v-if="record?.provider_state === WhatsappTemplateStates.Approved">
          <template #title>Asignar campañas y colas</template>

          <a-button class="table--button" type="link" @click="emit('openAssignModal', record)">
            <img class="assign__icon" :src="assignQueueImage" alt="add queue to template icon" />
          </a-button>
        </a-tooltip>
      </div>
    </template>
  </a-table>
  <div v-else>
    <div class="not-content" v-if="optionsIdentifier.length == 0">
      <span
        >Integra un nuevo número identificador de WhatsApp para poder solicitar plantillas
        predefinidas.</span
      >
      <a-button @click="toChannels"> Ir a mis canales </a-button>
    </div>
    <div class="not-content" v-else>
      <span
        >Solicita tus primeras plantillas preestablecidas para que sean enviadas por tus usuarios
        desde Workspace o Estrategias.</span
      >
      <a-button
        @click="emit('openCreateModal', selectedIdentifier)"
        :disabled="(optionsIdentifier.length == 0 && isViewTemplates) || !selectedIdentifier"
      >
        Solicitar plantilla
      </a-button>
    </div>
  </div>
  <!-- <div v-if="selectedIdentifier == null" class="TableEmpty">
    {{ isViewTemplates ? customLocale.emptyText : "" }}
  </div>

  <div v-else class="TableEmpty">{{ customLocale.selectedText }}</div> -->

  <RestrictionChangePlanModal
    title="Realiza un upgrade y accede a todo el poder de Beex."
    v-model:open="openRestriction"
  >
    <template #description>
      Tu plan {{ PlanTypeText[plan] }} no incluye este módulo.
      Solicita un upgrade tu plan para potenciar tu ambiente de trabajo.
    </template>
  </RestrictionChangePlanModal>
</template>

<script setup>
import { ref, createVNode, watch, inject, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Modal, message } from "ant-design-vue";
import _ from "lodash";
import {
  SearchOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  EditOutlined,
} from "@ant-design/icons-vue";

import {
  templateCategoriesOptions,
  templateStatusOptions,
  templateStatesOptions,
} from "@/app/channels/utils/selectOptions";
import { whatsappTemplateTableColumns } from "@/app/channels/utils/constants";
import {
  WhatsappTemplateStatus,
  WhatsappTemplateStates,
  WhatsappTemplateTypes,
  WhatsappTemplateTypeButtons,
  WhatsappTemplateTypeSpecificButtons,
} from "@/app/channels/utils/enums";
import { updateTemplate } from "@/app/channels/services";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";

import { fetchAllChannelsCustomers } from "@/app/channels/services";
import assignQueueImage from "@/app/channels/assets/assign-queue.svg?url";
import IconLink from "@/app/common/components/atoms/IconLink.vue";
import IconPhone from "@/app/common/components/atoms/IconPhone.vue";
import IconMessage from "@/app/common/components/atoms/IconMessage.vue";
import IconWhatsappVoice from "@/app/common/components/atoms/IconWhatsappVoice.vue";

import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { PlanType, PlanTypeText } from "@/app/common/types";
import { ActionPlanStrategy } from "@/app/common/utils/enums";
import { useStore } from "vuex";
import EventBus from "@/app/shared/utils/eventBus";

const stateTag = {
  [WhatsappTemplateStates.Pending]: { text: "Pendiente", color: "default" },
  [WhatsappTemplateStates.Approved]: { text: "Aprobado", color: "green" },
  [WhatsappTemplateStates.Rejected]: { text: "Rechazado", color: "red" },
  [WhatsappTemplateStates.Failed]: { text: "Fallido", color: "warning" },
};
const selectedIdentifier = ref(null);

const optionsIdentifier = ref([]);

defineProps({
  data: {
    type: Array,
    required: true,
  },
  loadingTable: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const emit = defineEmits(["update"]);

const { scrollStyle } = useScrollResize({ headerMargin: 370 });
const route = useRoute();
const router = useRouter();
const associate = route.query.associate;

const isViewTemplates = computed(() => route.path == "/channels/template");

const formRef = ref(null);
const formState = inject("filters");

const store = useStore();

const user = computed(() => store.getters["getDataUser"]);
const plan = computed(() => user.value?.change_custom_plan?.plan);
const openRestriction = ref(false);

const validateActionPlanHeader = () => {
  if (plan.value == PlanType.Voice) {
    store.commit("SET_ACTION_PLAN_HEADER", ActionPlanStrategy.Upgrade);
  } else {
    store.commit("SET_ACTION_PLAN_HEADER", null);
  }
};

EventBus.on("SHOW_RESTRICTION_PLAN", () => {
  openRestriction.value = true
});

watch(user, () => {
  validateActionPlanHeader();
})

watch(plan, () => {
  validateActionPlanHeader();
})

const updateTableData = () => emit("update", selectedIdentifier.value);

const updateStatus = async ({ templateId, body } = {}) => {
  const isActivating = body.status === WhatsappTemplateStatus.Active;
  const successText = isActivating ? "activó" : "inactivó";
  const errorText = isActivating ? "activar" : "inactivar";

  try {
    await updateTemplate({ templateId, body });
    message.success(`Se ${successText} la plantilla`);
    updateTableData();
  } catch (error) {
    const errorMessage = error?.getErrorMessage() || `No se pudo ${errorText} la plantilla`;

    message.error(errorMessage);
    console.error(error);
  }
};

const handleToggleState = async (record) => {
  const { id: templateId, status } = record;

  if (status === WhatsappTemplateStatus.Inactive) {
    return await updateStatus({ templateId, body: { status: WhatsappTemplateStatus.Active } });
  }

  Modal.confirm({
    icon: createVNode(QuestionCircleOutlined),
    title: "¿Estás seguro de inactivar esta plantilla?",
    content: "Recuerda que esta plantilla ya no podrá ser usada por ningún usuario.",
    okText: "Inactivar",
    onOk: async () =>
      await updateStatus({ templateId, body: { status: WhatsappTemplateStatus.Inactive } }),
  });
};

const toChannels = () => {
  router.push("/channels/board/");
};

const typeButtonsText = (type) => {
  if (type == WhatsappTemplateTypeButtons.QuickReply) {
    return 'Respuesta rápida';
  } else {
    return 'Llamada a la acción';
  }
}

onMounted(async () => {
  validateActionPlanHeader();
  optionsIdentifier.value = await fetchAllChannelsCustomers(3, null, 1);
});

onUnmounted(() => {
  store.commit("SET_ACTION_PLAN_HEADER", null);
});

watch(
  formState,
  _.debounce(() => updateTableData(), 600),
  { deep: false }
);

const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/conexiones/plantillas/whatsapp/`;
</script>

<style lang="sass" scoped>
h2
  font-weight: 600
  font-size: 16px
  color: $gray-9
  margin: 12px 0

.form
  &__input
    width: 280px

  &__item
    min-width: 180px

.table
  margin-top: 18px

  &--controls
    display: flex
    justify-content: space-between

  &--button
    padding: 0

  .actions__wrapper
    display: flex
    gap: 8px

.assign__icon
  width: 18px
  position: relative
  top: -1px

.info
  color: $gray-6
  font-style: italic

.subtitulo
  display: inline

.TableEmpty
  color: $gray-9
  text-align: center

.not-content
  height: calc(100vh - 350px)
  display: flex
  align-items: center
  justify-content: center
  flex-direction: column
  span
    font-size: 22px
    width: 456px
    text-align: center
    margin-bottom: 20px
    weight: 400
    color: $gray-7

.button
  &__name
    display: flex
    align-items: center
    &-type
      font-weight: 500
    span
      margin-left: 5px
  &__detail
    margin-left: 23px
  &__call
    margin-left: 23px
    font-style: italic
    color: #000000A6
</style>
